import { useLazyQuery, useQuery } from '@apollo/client';
import { IDropdownOption, Stack, Text, useTheme } from '@fluentui/react';
import { HelpIcon } from 'common/trainingSystem/HelpIcon';
import { useEffect, useState } from 'react';
import {
  CompanyConfigurationCategories,
  CompanyConfigurationCategories_companyConfigurationCategories_nodes,
} from './__generated__/CompanyConfigurationCategories';
import { BusinessRulesLeftPanel } from './BusinessRulesLeftPanel';
import COMPANY_CONFIGURATION_CATEGORIES from './CompanyConfigurationCategories.graphql';
import { useStyles } from './index.styles';
import { SelectedCategoryRulesView } from './SelectedCategoryRulesView';
import { EditRuleViewValues } from './SelectedCategoryRulesView/types';
import COMPANY_CONFIGURATION_WARNING from './CompanyConfigurationWarning.graphql';
import { CompanyConfigurationWarning } from './__generated__/CompanyConfigurationWarning';

const LEFT_PANEL_WIDTH = 300;

export const BusinessRules: React.FC = () => {
  const { data: companyConfigurationCategoriesData } =
    useQuery<CompanyConfigurationCategories>(COMPANY_CONFIGURATION_CATEGORIES);
  const [
    fetchCompanyConfigurtionWarning,
    { data: companyConfigurationWarningData },
  ] = useLazyQuery<CompanyConfigurationWarning>(COMPANY_CONFIGURATION_WARNING, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const theme = useTheme();
  const styles = useStyles();
  const windowHeight = window.innerHeight;
  const [selectedNode, setSelectedNode] = useState<
    | CompanyConfigurationCategories_companyConfigurationCategories_nodes
    | undefined
  >(undefined);
  const [isDirty, setIsDirty] = useState(false);
  const [values, setValues] = useState<EditRuleViewValues | undefined>(
    undefined
  );

  const businessRuleCategoriesOptions =
    companyConfigurationCategoriesData?.companyConfigurationCategories?.nodes.map(
      (item) =>
        ({
          key: item.configurationCategoryId,
          text: item.name,
        } as IDropdownOption)
    ) || [];

  useEffect(() => {
    if (!selectedNode && companyConfigurationCategoriesData)
      setSelectedNode(
        companyConfigurationCategoriesData.companyConfigurationCategories
          ?.nodes[0]
      );
  }, [companyConfigurationCategoriesData]);

  return (
    <Stack style={{ padding: 20 }}>
      <Stack horizontal grow tokens={{ childrenGap: 0 }}>
        <Stack>
          <Stack
            horizontal
            verticalAlign="center"
            tokens={{ childrenGap: 7 }}
            style={{ paddingBottom: 30 }}
          >
            <Text variant="xLarge">Business Rules</Text>
            <HelpIcon appLinkType="COMPANY_BUSINESS_RULES" />
          </Stack>
          <Stack
            className={styles.customScroll}
            styles={{
              root: {
                width: LEFT_PANEL_WIDTH,
                height: windowHeight - 150,
              },
            }}
          >
            <BusinessRulesLeftPanel
              data={companyConfigurationCategoriesData}
              setSelectedNode={setSelectedNode}
              isDirty={isDirty}
              values={values}
              selectedNode={selectedNode}
              refreshConfigurationWarning={fetchCompanyConfigurtionWarning}
            />
          </Stack>
        </Stack>
        <div
          style={{
            width: '1px',
            backgroundColor: theme.palette.neutralLighter,
            zIndex: 1,
            marginTop: 60,
          }}
        />
        {selectedNode && (
          <SelectedCategoryRulesView
            selectedNode={selectedNode}
            businessRuleCategoriesOptions={businessRuleCategoriesOptions}
            onDirtyChange={(isDirty, values) => {
              setIsDirty(isDirty);
              setValues(values);
            }}
            fetchCompanyConfigurtionWarning={fetchCompanyConfigurtionWarning}
            companyConfigurationWarningData={companyConfigurationWarningData}
          />
        )}
      </Stack>
    </Stack>
  );
};
